/**
 * `canvasCommentColors` — the review palette, in numbers a canvas can paint.
 *
 * WHY THIS FILE EXISTS. The DOM read view is gone (de16828); the paginated
 * canvas is the only document surface. On the DOM the comment highlight was
 * CSS — `styles/annotations.css` painted a per-type wash, a per-type
 * border-bottom, a hover glow and a 1.4s bloom, all through `var()`/`color-mix()`
 * tokens. A `<canvas>` has none of that: the only thing the engine can be handed
 * is a CONCRETE fill string per rect.
 *
 * AND THE CONCRETENESS IS LOAD-BEARING. `CanvasRenderingContext2D.fillStyle`
 * cannot parse `var(--x)` or `color-mix(…)`, and it does not throw on either —
 * the assignment is a SILENT no-op that leaves the previous fill in place. So a
 * canvas fed a theme token paints nothing while every "setHighlights was called"
 * assertion stays green. {@link isCanvasPaintable} is the tripwire against that,
 * and `canvasCommentColors.test.ts` asserts that EVERY colour this module can
 * emit — including on every failure path — passes it.
 *
 * HOW A TOKEN BECOMES A FILL. The `-wash`/`-glow` tokens are `color-mix()`
 * expressions, so they are consumed for their ALPHA ONLY; the colour itself comes
 * from the plain-hex `-hue` token. Both are read through a live probe
 * (`getComputedStyle` on a hidden span), so whichever theme is mounted is the
 * theme that gets painted — light and dark carry DIFFERENT alphas (18%/32% vs
 * 22%/38% for `comment`) and neither is hardcoded as "the" theme. The literal
 * tables below are FALLBACKS for when there is no live CSS at all (a detached
 * host, a test runtime), never the primary source.
 *
 * RULE ZERO / HR13. Nothing generic is invented here: the probe + `withAlpha`
 * pattern was already in `ReadCanvas.tsx` (privately, for the selection tint) and
 * is LIFTED here so there is ONE resolver rather than two drifting copies —
 * `ReadCanvas` now imports it. The values are elaborate's own design tokens, so
 * this is product policy and belongs product-side; what is generic (painting a
 * per-rect fill, running the bloom) is the engine's `HighlightRect` /
 * `flashHighlights` and is NOT reimplemented here.
 */

import { isCanvasPaintable } from "@norkron/web-page-layout";

/** The three review types that carry their own hue (rich-comments §12). */
export type CommentTypeKey = "comment" | "suggestion" | "request-for-change";

/** Anchor confidence — the bloom's colour source. */
export type BloomAnchorState = "exact" | "moved" | "orphan";

/** Resolve ONE CSS custom property name to a concrete computed colour string.
 *  `""` when nothing can be resolved (a detached host, no stylesheet). */
export type ColorResolver = (cssVar: string) => string;

/** The three fills one commented span needs on the canvas. */
export interface CommentPaint {
  /** At rest — the DOM's `background: var(--ela-type-{t}-wash)`. */
  readonly rest: string;
  /** Hover / selected — the DOM's `:hover` + `[data-linked-hover]` glow. */
  readonly glow: string;
  /** The peak of the one-shot bloom, faded to transparent by the engine —
   *  the DOM's `@keyframes ela-anno-bloom` 0% stop. */
  readonly bloom: string;
}

/** At-rest alphas, LIGHT theme (`:root` in `theme/ela-theme.css`). */
export const REST_ALPHA: Readonly<Record<CommentTypeKey, number>> = {
  comment: 0.18,
  suggestion: 0.3,
  "request-for-change": 0.18,
};

/** Hover/selected alphas, LIGHT theme. */
export const GLOW_ALPHA: Readonly<Record<CommentTypeKey, number>> = {
  comment: 0.32,
  suggestion: 0.48,
  "request-for-change": 0.3,
};

/** The un-typed `.docz-comment.ela-annotation` rule: a legacy span with no
 *  `data-comment-type` painted the SUGGESTION hue at 18%, hovering to 34%. */
export const LEGACY_REST_ALPHA = 0.18;
export const LEGACY_GLOW_ALPHA = 0.34;

/** `@keyframes ela-anno-bloom` 0%: `…var(--ela-anchor-exact) 90%, transparent`. */
export const BLOOM_PEAK_ALPHA = 0.9;

/** `animation: ela-anno-bloom 1.4s ease` — the DOM bloom's duration, restated in
 *  ms for the engine's `flashHighlights`. Equals the engine's own
 *  `DEFAULT_FLASH_DURATION_MS`; passed explicitly so the parity is stated at the
 *  call site rather than inherited from a default that could move. */
export const BLOOM_DURATION_MS = 1400;

/** `:root` literals — used ONLY when the live probe resolves nothing. */
export const TYPE_HUE_FALLBACK: Readonly<Record<CommentTypeKey, string>> = {
  comment: "#3b82f6",
  suggestion: "#eab308",
  "request-for-change": "#dc2626",
};

/** `:root` literals for the anchor-confidence hues. These are the SAME literals
 *  `annotations.css` states inline as its `var(--ela-anchor-exact, #fbefc2)`
 *  fallback, so the canvas degrades exactly as the DOM did. */
export const ANCHOR_HUE_FALLBACK: Readonly<Record<BloomAnchorState, string>> = {
  exact: "#fbefc2",
  moved: "#e7c9a0",
  orphan: "#ede9e2",
};

const TYPE_KEYS: readonly CommentTypeKey[] = [
  "comment",
  "suggestion",
  "request-for-change",
];

/**
 * Can `CanvasRenderingContext2D.fillStyle` actually paint this string?
 *
 * RE-EXPORTED FROM THE ENGINE, not re-implemented (RULE ZERO). The predicate is
 * a property of the canvas spec, not of elaborate's palette, so
 * `@norkron/web-page-layout` owns the ONE definition and every consumer asks it
 * the same question. Surfaced here because this module is where a colour is
 * BUILT — the assertion that every fill it emits is paintable belongs next to
 * the code that emits them.
 *
 * WHY IT MATTERS MORE THAN IT LOOKS. `fillStyle` is not a CSS property: `var()`
 * is substituted by the cascade (which a canvas is not part of), `color-mix()`
 * is not in the accepted grammar, and the spec says an invalid assignment is
 * IGNORED — so `fillStyle` KEEPS ITS PREVIOUS VALUE. An unpaintable colour does
 * not paint nothing; it paints the LAST comment's colour. Measured in Chrome by
 * the engine's own narrate drive: a band handed a token came out
 * `rgba(222,39,39,0.18)`, the red of the rect above it. A plausible highlight in
 * the wrong hue is a bug nobody files.
 */
export { isCanvasPaintable };

/** Expand a 3/4-digit hex the way CSS does (`#abc` → `#aabbcc`). */
function expandHex(hex: string): string {
  if (hex.length !== 3 && hex.length !== 4) return hex;
  let out = "";
  for (const ch of hex) out += ch + ch;
  return out;
}

/**
 * The `[r, g, b]` channels of a concrete colour, or `null` when the string is
 * not one this module can safely re-tint.
 *
 * `null` rather than "pass the input through" is deliberate: the helper this
 * replaces returned its argument unchanged on a parse miss, which handed
 * `fillStyle` a `var(…)` and painted nothing. A caller that gets `null` must
 * fall back to a literal it CAN paint.
 */
export function rgbTripleOf(color: string): [number, number, number] | null {
  const c = color.trim();
  if (c.length === 0) return null;
  if (c.startsWith("#")) {
    const hex = expandHex(c.slice(1));
    if (hex.length !== 6 && hex.length !== 8) return null;
    if (!/^[0-9a-f]+$/i.test(hex)) return null;
    return [
      Number.parseInt(hex.slice(0, 2), 16),
      Number.parseInt(hex.slice(2, 4), 16),
      Number.parseInt(hex.slice(4, 6), 16),
    ];
  }
  // `color(srgb r g b / a)` — what Chrome's `getComputedStyle` returns for a
  // `color-mix()` result, i.e. for EVERY comment-type wash in `annotations.css`.
  // Channels are 0..1 floats here, not 0..255, and missing this form is not a
  // cosmetic gap: it made the whole washed ancestor unreadable, so the resolver
  // fell through to a colour that was not what the reader sees.
  const srgb = /^color\(\s*srgb\s+([^)]*)\)$/i.exec(c);
  if (srgb) {
    const p = (srgb[1] ?? "").replace("/", " ").split(/[\s,]+/).filter(Boolean);
    if (p.length < 3) return null;
    const n = p.slice(0, 3).map((v) =>
      v.endsWith("%") ? Number.parseFloat(v) / 100 : Number.parseFloat(v),
    );
    if (n.some((v) => !Number.isFinite(v))) return null;
    return [
      Math.round((n[0] ?? 0) * 255),
      Math.round((n[1] ?? 0) * 255),
      Math.round((n[2] ?? 0) * 255),
    ];
  }
  const m = /^rgba?\(([^)]*)\)$/i.exec(c);
  if (!m) return null;
  // Both the legacy comma form and the modern space/slash form.
  const parts = (m[1] ?? "").replace("/", " ").split(/[\s,]+/).filter(Boolean);
  if (parts.length < 3) return null;
  const ch = parts.slice(0, 3).map((p) => {
    if (p.endsWith("%")) return (Number.parseFloat(p) / 100) * 255;
    return Number.parseFloat(p);
  });
  if (ch.some((n) => !Number.isFinite(n))) return null;
  return [Math.round(ch[0] ?? 0), Math.round(ch[1] ?? 0), Math.round(ch[2] ?? 0)];
}

/**
 * The alpha channel of a concrete colour, or `null` when it cannot be read.
 *
 * This is how the LIVE theme's ratio is obtained: `--ela-type-comment-wash`
 * computes to `rgba(59, 130, 246, 0.18)` under light and
 * `rgba(96, 165, 250, 0.22)` under dark, and only that number is taken from it.
 */
export function alphaOf(color: string): number | null {
  const c = color.trim();
  if (c.length === 0) return null;
  if (/^transparent$/i.test(c)) return 0;
  if (c.startsWith("#")) {
    const hex = expandHex(c.slice(1));
    if (hex.length === 8) return Number.parseInt(hex.slice(6, 8), 16) / 255;
    if (hex.length === 6) return 1;
    return null;
  }
  const m = /^(?:rgba?|color)\(([^)]*)\)$/i.exec(c);
  if (!m) return null;
  const body = m[1] ?? "";
  const slash = body.indexOf("/");
  if (slash >= 0) {
    const a = body.slice(slash + 1).trim();
    const n = a.endsWith("%") ? Number.parseFloat(a) / 100 : Number.parseFloat(a);
    return Number.isFinite(n) ? n : null;
  }
  const parts = body.split(/[\s,]+/).filter(Boolean);
  // `color(srgb x y z)` has a leading colourspace token; `rgb()`/`rgba()` do not.
  const numeric = parts.filter((p) => Number.isFinite(Number.parseFloat(p)));
  if (numeric.length < 4) return numeric.length >= 3 ? 1 : null;
  const a = numeric[3] ?? "";
  const n = a.endsWith("%") ? Number.parseFloat(a) / 100 : Number.parseFloat(a);
  return Number.isFinite(n) ? n : null;
}

/**
 * Re-tint `color` at `alpha`, or `null` when it cannot be parsed.
 *
 * Any alpha already on the input is REPLACED — the `-hue` tokens are opaque, but
 * a caller that passes a resolved `-wash` must not end up multiplying two alphas.
 */
export function withAlpha(color: string, alpha: number): string | null {
  const rgb = rgbTripleOf(color);
  if (!rgb) return null;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * A {@link ColorResolver} backed by a real hidden probe inside `host`.
 *
 * This is the ONLY way to read a theme token from script: custom properties are
 * not exposed as parsed colours, so the value is assigned to a real `color`
 * property on a throwaway span and read back off `getComputedStyle`, which
 * resolves `var()` — and `color-mix()` — to a concrete value.
 *
 * Results are memoised for the lifetime of the resolver, so a re-anchor pass over
 * N comments does 3 lookups, not 3N. Create a NEW resolver per pass and a theme
 * switch is picked up on the next repaint.
 */
export function createProbeResolver(host: HTMLElement): ColorResolver {
  const cache = new Map<string, string>();
  return (cssVar: string): string => {
    const hit = cache.get(cssVar);
    if (hit !== undefined) return hit;
    let out = "";
    const doc = host.ownerDocument;
    if (doc) {
      const probe = doc.createElement("span");
      probe.style.position = "absolute";
      probe.style.visibility = "hidden";
      probe.style.pointerEvents = "none";
      host.appendChild(probe);
      probe.style.color = `var(${cssVar})`;
      out = doc.defaultView?.getComputedStyle(probe).color ?? "";
      probe.remove();
    }
    cache.set(cssVar, out);
    return out;
  };
}

/** The typed key for a stored `comment.type`, or `null` for a legacy/unknown
 *  type — which takes the DOM's neutral `.docz-comment` rule, not a guess. */
export function commentTypeKey(raw: string): CommentTypeKey | null {
  return (TYPE_KEYS as readonly string[]).includes(raw)
    ? (raw as CommentTypeKey)
    : null;
}

/** Resolve `cssVar` into an rgba fill at `alpha`, falling back to `literal`. */
function tint(
  resolve: ColorResolver,
  cssVar: string,
  literal: string,
  alpha: number,
): string {
  return (
    withAlpha(resolve(cssVar), alpha) ??
    withAlpha(literal, alpha) ??
    `rgba(0, 0, 0, ${alpha})`
  );
}

/** The live alpha carried by `cssVar`, or `fallback` when it cannot be read. */
function alphaFrom(
  resolve: ColorResolver,
  cssVar: string,
  fallback: number,
): number {
  const a = alphaOf(resolve(cssVar));
  return a === null ? fallback : a;
}

export interface CommentPaintRequest {
  /** The stored `comment.type`. Unknown values take the legacy rule. */
  readonly type: string;
  /** The resolved anchor confidence — drives the bloom hue only. */
  readonly state: BloomAnchorState;
  readonly resolve: ColorResolver;
}

/**
 * The three fills for one commented span, mirroring `styles/annotations.css`.
 *
 * WHAT DOES NOT COME ACROSS. The DOM rule also painted a
 * `border-bottom: 2px solid var(--ela-type-{t}-hue)` (3px on hover, DASHED for a
 * `moved` anchor). The engine's highlight layer fills bands; it has no per-rect
 * stroke, so the underline — and with it the dashed "this anchor moved" cue — is
 * NOT expressible today and is reported as a gap rather than approximated with a
 * different fill, which would silently change what a colour MEANS.
 */
export function commentPaint(req: CommentPaintRequest): CommentPaint {
  const key = commentTypeKey(req.type);
  const hueVar = key ? `--ela-type-${key}-hue` : "--ela-type-suggestion";
  const hueLiteral = TYPE_HUE_FALLBACK[key ?? "suggestion"];
  const hue = resolveLiteral(req.resolve, hueVar, hueLiteral);

  const restAlpha = key
    ? alphaFrom(req.resolve, `--ela-type-${key}-wash`, REST_ALPHA[key])
    : LEGACY_REST_ALPHA;
  const glowAlpha = key
    ? alphaFrom(req.resolve, `--ela-type-${key}-glow`, GLOW_ALPHA[key])
    : LEGACY_GLOW_ALPHA;

  const bloomVar =
    req.state === "exact" ? "--ela-anchor-exact" : "--ela-anchor-moved";
  const bloomLiteral =
    ANCHOR_HUE_FALLBACK[req.state === "exact" ? "exact" : "moved"];

  return {
    rest: withAlpha(hue, restAlpha) ?? `rgba(0, 0, 0, ${restAlpha})`,
    glow: withAlpha(hue, glowAlpha) ?? `rgba(0, 0, 0, ${glowAlpha})`,
    bloom: tint(req.resolve, bloomVar, bloomLiteral, BLOOM_PEAK_ALPHA),
  };
}

// ─── the SHEET a fragment is painted on ──────────────────────────────────────

/**
 * The paper a fragment surface mounted on a review card asks for: NO PAPER.
 *
 * ## Owner order, 2026-07-30, verbatim
 *
 * *"the canvas with text in review notes STILL have and I ORDERED YOU TO MAKE
 * THEM TRANSPARENT!!!"*
 *
 * Two wrong answers preceded this one and both were OPAQUE, which is why the
 * constant exists rather than a literal at two call sites:
 *
 *  1. the engine's own default, `#ffffff` — export white. Right for a DOCUMENT
 *     (a page is a page, and that is what will print), and measured in Chrome on
 *     `/w/wettformat/article/1` in dark theme as a white sheet punched into a
 *     `rgb(28, 31, 36)` card.
 *  2. the card's own composited colour (`rgb(76, 49, 53)` dark /
 *     `rgb(249, 216, 216)` light), resolved by walking the ancestor chain. It
 *     LOOKED right — and that is the trap: a fill that matches the card is
 *     indistinguishable from no fill by eye, so it satisfied a screenshot while
 *     still being a painted background. It also had to be recomputed on every
 *     theme switch, every card state and every per-type wash, i.e. a second
 *     statement of a colour `annotations.css` already owns.
 *
 * `'transparent'` is neither. The engine `clearRect`s the band BEFORE the paper
 * fill (`render.ts` §renderBand) and `fillStyle = 'transparent'` composites
 * nothing, so the tile's blank pixels keep **alpha 0** and whatever the card's
 * CSS paints behind the canvas is what the reader sees — one owner of that
 * colour, the stylesheet.
 *
 * ⚠️ WHAT IT COSTS, stated so nobody "fixes" it: glyphs on a transparent canvas
 * lose subpixel antialiasing (that needs an opaque backdrop) and fall back to
 * grayscale AA. Expected and accepted. ⛔ Reintroducing a fill to recover it is
 * the bug, not the cure.
 */
export const FRAGMENT_PAPER = "transparent";

/**
 * The default INK for a fragment surface sitting ON A CARD, read from the LIVE
 * cascade — or `null` when the cascade cannot be read.
 *
 * ## ⛔ THE INK DOES NOT GO AWAY WITH THE PAPER
 *
 * The engine's default ink is `#000000`. On a transparent canvas over a dark
 * card that is black on near-black: a surface with a perfect model behind it and
 * nothing a reader can see. Dropping the paper WITHOUT keeping this is strictly
 * worse than the white sheet both fixes replaced, so the ink stays resolved and
 * passed while only the background stops being painted.
 *
 * ## Why the DOM is the source, not a token name
 *
 * Plain inheritance: `getComputedStyle(host).color` is the colour the card's own
 * text is already set in, so the glyphs follow the card in whatever theme and
 * whatever card state, with no token list here to drift from `annotations.css`.
 *
 * Run through {@link isCanvasPaintable} before it is returned: a colour
 * `fillStyle` cannot parse is a SILENT no-op that leaves the previous fill in
 * force, so an unresolvable value must degrade to "pass nothing" (the engine's
 * documented black) rather than to "paint the last surface's colour".
 */
export function surfaceInk(host: HTMLElement): string | null {
  const view = host.ownerDocument?.defaultView;
  if (!view) return null;
  const ink = view.getComputedStyle(host).color;
  return isCanvasPaintable(ink) ? ink : null;
}

/** The concrete colour behind `cssVar`, or `literal` when the probe misses. */
function resolveLiteral(
  resolve: ColorResolver,
  cssVar: string,
  literal: string,
): string {
  const live = resolve(cssVar);
  return rgbTripleOf(live) ? live : literal;
}
